/**
 * Numeral canonicalization (D133): a number word and its digits are ONE
 * token to the resolver, so `locker 11` answers wherever `locker eleven`
 * does — without every pack hand-authoring both spellings on every numbered
 * thing.
 *
 * @remarks
 * Applied at COMPARISON, on both sides — the phrase's words and the catalog's
 * names, aliases and adjectives — never to the tokens themselves, so a teach
 * still quotes exactly what the player typed (D26) and a clarification's
 * matched tokens are the player's words. The canonical form is the digit
 * string: it is what a player who reads "Locker eleven" and types `11` has in
 * mind, and it needs no inflection.
 *
 * The table is deliberately the numbers a game names in prose — zero through
 * twenty, the tens, and their hyphenated compounds (`twenty-one`). Hundreds
 * and thousands are not here: prose that says "four hundred and twelve steps"
 * is not naming a thing a player will type a number for, and a table that
 * tried to parse arithmetic would be a different feature wearing this one's
 * name. English only, like the vocabulary it serves.
 *
 * @packageDocumentation
 */

/** The number words a game names in prose, by value. */
const NUMBER_WORDS: readonly string[] = [
	'zero',
	'one',
	'two',
	'three',
	'four',
	'five',
	'six',
	'seven',
	'eight',
	'nine',
	'ten',
	'eleven',
	'twelve',
	'thirteen',
	'fourteen',
	'fifteen',
	'sixteen',
	'seventeen',
	'eighteen',
	'nineteen',
	'twenty',
];

/** The tens beyond twenty, by their multiplier (index 3 = thirty). */
const TENS_WORDS: Readonly<Record<string, number>> = {
	eighty: 80,
	fifty: 50,
	forty: 40,
	ninety: 90,
	seventy: 70,
	sixty: 60,
	thirty: 30,
};

/** Every single-word numeral → its digits. */
const DIGITS_BY_WORD: ReadonlyMap<string, string> = new Map([
	...NUMBER_WORDS.map((word, value): [string, string] => [word, String(value)]),
	...Object.entries(TENS_WORDS).map(([word, value]): [string, string] => [word, String(value)]),
]);

/**
 * The canonical form of one folded word: its digits when it is a number word
 * (or a hyphenated tens compound), the word itself otherwise.
 *
 * @param word - One folded token
 * @returns The word, or its digits
 */
export const canonicalizeNumeralWord = (word: string): string => {
	const digits = DIGITS_BY_WORD.get(word);

	if (digits !== undefined) {
		return digits;
	}

	// `twenty-one` … `ninety-nine`: a tens word, a hyphen, a unit word.
	const [tensWord, unitWord, ...rest] = word.split('-');
	// `split` always yields a first element, so the tens word is present
	// whenever the word is — a guard for its absence would be a branch nothing
	// can enter, and `String` is identity for the string it always is.
	const tens = TENS_WORDS[String(tensWord)] ?? (tensWord === 'twenty' ? 20 : undefined);
	const unit = unitWord === undefined ? undefined : NUMBER_WORDS.indexOf(unitWord);

	return tens !== undefined && unit !== undefined && unit >= 1 && unit <= 9 && rest.length === 0
		? String(tens + unit)
		: word;
};

/**
 * Canonicalize every word of a folded, space-joined name or phrase.
 *
 * @param text - Folded words, space-separated
 * @returns The same words with numerals in canonical form
 */
export const canonicalizeNumerals = (text: string): string => text.split(' ').map(canonicalizeNumeralWord).join(' ');
